import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useAppDispatch } from 'store/hooks';

import { getLessonById, removeLessonById } from 'store/lesson/actions';

import { currentLessonSelector } from 'store/lesson/selectors';

import { showToast } from 'helpers/showToast';

import { getTranslation } from 'helpers/getTranslation';
import { getUnknownErrorMessage } from 'helpers/unknownErrorMessage';

import { useModal } from 'hooks/useModal';

import { DefaultModal } from 'components/DefaultModal';

import CatalogLessonProvider from './CatalogLessonProvider';

const CatalogLessonProviderContainer = () => {
  const { lessonId } = useParams();

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const lesson = useSelector(currentLessonSelector);

  const [loading, setLoading] = useState(true);

  const [
    confirmModalOpen,
    openConfirmModal,
    closeConfirmModal,
    confirmModalData,
  ] = useModal({
    defaultOpen: false,
  });

  useEffect(() => {
    setLoading(true);

    Promise.all([dispatch(getLessonById({ lessonId }))]).finally(() => {
      setTimeout(() => {
        setLoading(false);
      }, 100);
    });
  }, [lessonId]);

  const handleConfirmRemoveClick = async () => {
    try {
      await dispatch(removeLessonById({ lessonId: confirmModalData.lessonId }));

      closeConfirmModal();

      showToast({
        data: {
          variant: 'success',
          title: getTranslation('common.toast.remove_lesson.title'),
          text: getTranslation('common.toast.remove_lesson.text'),
        },
      });

      navigate('/lessons');
    } catch (error) {
      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getUnknownErrorMessage(
            error,
            getTranslation('common.toast.error.text'),
          ),
        },
      });
    }
  };

  const handleRemoveClick = (data: any) =>
    openConfirmModal({
      lessonId: data.id,
      title: getTranslation('providers.lesson_provider.confirm_modal.title'),
      text: getTranslation('providers.lesson_provider.confirm_modal.text', {
        title: data.title || getTranslation('common.text.untitled'),
      }),
    });

  return (
    <>
      <CatalogLessonProvider
        loading={loading}
        lesson={lesson}
        onRemoveClick={handleRemoveClick}
      />
      <DefaultModal
        {...confirmModalData}
        open={confirmModalOpen}
        confirmText={getTranslation(
          'providers.lesson_provider.confirm_modal.confirm_text',
        )}
        onConfirmClick={handleConfirmRemoveClick}
        onCancelClick={closeConfirmModal}
        onClose={closeConfirmModal}
      />
    </>
  );
};

export default React.memo(CatalogLessonProviderContainer);
